import { Grid } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  PolicyTimeslot_DaysEnum,
  PolicyRule_RuleUnitsEnum,
  PolicyRule_VehicleTypesEnum,
  PolicyTimeslot,
} from 'src/api/gen/api'
import { IPolicyRuleSpeed } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { isEndTimeBeforeStartTime } from 'src/commons/policies/utils'
import NumericInput from 'src/components/Input/NumericInput'
import { SimpleTable } from 'src/components/Table'
import { useAgencySettings } from 'src/layout/AgencySettings'
import SETTINGS from 'src/settings'

import { FieldTitle } from '../../commons'
import { GeographiesField, RuleNameField, VehicleTypesField } from '../fields'

import TimeSlotForm from './TimeSlot'

const MAXIMUM_SPEED = 1000 // in km.h

type Row = [
  TRulesValidation,
  string, // name,
  string, // ruleId
  PolicyRule_VehicleTypesEnum[], // vehicleTypes
  number, // maximum speed
  number, // buffer speed
  string[], // geographies
  string[], // buffered_geographies
  PolicyRule_RuleUnitsEnum | undefined // rule unit
]

function shouldDisplayError(maximum: number) {
  return maximum < 0 || maximum > MAXIMUM_SPEED
}

function shouldDisplayBufferError(maximum: number, buffer: number) {
  return buffer < 0 || (buffer > 0 && buffer < maximum)
}

function SpeedLimitRule(props: IBaseTableProps<IPolicyRuleSpeed>) {
  const { t } = useTranslation()
  const agencySettings = useAgencySettings()

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleSpeed) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRules: IPolicyRuleSpeed[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      console.info(newRules)
      props.handleChange(newRules)
    }

  function handleChangeTimeslot(timeslot: PolicyTimeslot, index: number, key: keyof PolicyTimeslot) {
    return (val: string | PolicyTimeslot_DaysEnum[] | null): void => {
      const timeslotsToUpdate: PolicyTimeslot[] = props.rules[0].timeslots
      timeslotsToUpdate[index] = { ...timeslot, [key]: val }
      const newRules: IPolicyRuleSpeed[] = [{ ...props.rules[0], ['timeslots']: timeslotsToUpdate }]
      props.handleChange(newRules)
    }
  }

  const HEADERS = [
    { title: t('rule.name') },
    { title: t('rule.count.vehicle_types') },
    { title: t('rule.speed.maximum') },
    { title: t('rule.speed.buffer'), tooltip: t('rule.speed.buffer.tooltip') },
    { title: t('rule.geographies') },
  ] as Array<{ title: string; tooltip: string }>

  const items = props.rules.map(
    (rule: IPolicyRuleSpeed, idx): Row => [
      props.validation[idx],
      rule.name,
      rule.rule_id,
      rule.vehicle_types,
      rule.maximum || 0,
      rule.buffer_speed || 0,
      rule.geographies,
      rule.buffered_geographies,
      rule.rule_units,
    ]
  )

  return (
    <>
      <SimpleTable
        headerRow={HEADERS.map((header) => (
          <TableCell key={`key-${header.title}`}>
            <FieldTitle title={header.title} tooltip={header?.tooltip ?? ''} />
          </TableCell>
        ))}
        items={items}
        rowRenderer={([ruleValidation, name, ruleId, vehicleTypes, maximumSpeed, bufferSpeed, geographies]: Row) => (
          <>
            <TableCell style={{ height: '100px', verticalAlign: 'bottom' }}>
              <RuleNameField
                value={name}
                handleChange={handleChangeRule(ruleId, 'name')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('name')}
                helperText={ruleValidation.getFieldErrorMsg('name')}
              />
            </TableCell>

            <TableCell style={{ height: '100px', verticalAlign: 'bottom' }}>
              <VehicleTypesField
                value={vehicleTypes}
                handleChange={handleChangeRule(ruleId, 'vehicle_types')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('vehicle_types')}
                helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
              />
            </TableCell>

            <TableCell
              style={{
                height: '100px',
                verticalAlign: 'bottom',
                paddingTop: '61px',
              }}
            >
              <NumericInput
                id="speed_limit_maximum_speed"
                value={maximumSpeed}
                onChange={handleChangeRule(ruleId, 'maximum')}
                disabled={props.disabled}
                error={shouldDisplayError(maximumSpeed)}
                helperText={
                  (shouldDisplayError(maximumSpeed) &&
                    (t('pricingPolicy.ruleBadValue', { maximum: MAXIMUM_SPEED }) as string)) ||
                  ''
                }
                currency={t('rule.speed.unit_measure')}
                unit={agencySettings.speed_unit_display}
              />
            </TableCell>

            <TableCell
              style={{
                height: '100px',
                verticalAlign: 'bottom',
                paddingTop: '61px',
              }}
            >
              <NumericInput
                value={bufferSpeed}
                onChange={handleChangeRule(ruleId, 'buffer_speed')}
                disabled={props.disabled}
                error={shouldDisplayBufferError(maximumSpeed, bufferSpeed)}
                helperText={
                  (shouldDisplayBufferError(maximumSpeed, bufferSpeed) &&
                    (t('rule.speed.belowMaximum', { value: maximumSpeed }) as string)) ||
                  t('rule.speed.buffer.helper')
                }
                currency={t('rule.speed.unit_measure')}
                unit={agencySettings.speed_unit_display}
              />
            </TableCell>

            <TableCell style={{ maxWidth: '400px' }}>
              <GeographiesField
                id="speed_limit_geographies"
                value={geographies}
                allGeographies={props.allGeographies}
                handleChange={handleChangeRule(ruleId, 'geographies')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('geographies')}
                helperText={ruleValidation.getFieldErrorMsg('geographies')}
              />
            </TableCell>
          </>
        )}
      />
      {SETTINGS.app.enable_timeslots && (
        <Grid container spacing={3}>
          {props.rules[0].timeslots.map((timeslot, index) => {
            return (
              <Grid item xs={3} key={index}>
                <Grid container direction="column" spacing={1} style={{ marginTop: '13px' }}>
                  <Grid item component="h3">
                    {t('rule.time_slot_count', { index: index + 1 })}
                  </Grid>
                  <Grid item>
                    <TimeSlotForm
                      timeslot={timeslot}
                      handleChangeDays={handleChangeTimeslot(timeslot, index, 'days')}
                      handleChangeStartTime={handleChangeTimeslot(timeslot, index, 'start_time')}
                      handleChangeEndTime={handleChangeTimeslot(timeslot, index, 'end_time')}
                      handleRemoveTimeSlot={() => props.handleRemoveTimeSlot(index)}
                      disabled={props.isPublished && props.disabled}
                      error={isEndTimeBeforeStartTime(timeslot)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      )}
    </>
  )
}

export default SpeedLimitRule
